import { ListItemIcon } from '@mui/material';
import { first } from 'lodash';
import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { User01Icon } from '@/components/icons/User01Icon';
import { formatCreatedFromEntities } from '@/modules/entities/testamentaryEntities/testamentaryEntities.utils';
import { EntityStage } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import { SURVIVING_SPOUSE_SENTINEL } from './EntityBeneficiariesSubform.constants';
import { EntityBeneficiariesFormBeneficiary } from './EntityBeneficiariesSubform.types';
import { BeneficiariesFormOptionsQuery } from './graphql/Beneficiaries.generated';

interface UseGetBeneficiaryOptionsReturnValue {
  options: TypeaheadSelectInputOption<string>[];
}

interface UseGetBeneficiaryOptionsParams {
  currentEntityId: string | null;
  queryData: BeneficiariesFormOptionsQuery | undefined;
  subformValues: EntityBeneficiariesFormBeneficiary[] | undefined;
  /**
   * Whether the household has two primary clients.
   * The "Surviving Spouse" option is only available for two-client households.
   */
  isTwoClientHousehold: boolean;
  /**
   * Whether to allow the "Surviving Spouse" option.
   * IMPORTANT: This should ONLY be true for subtrust (testamentary entity) beneficiary forms.
   * Do not enable this for regular entity beneficiaries.
   */
  allowSurvivingSpouseOption: boolean;
}

export function useGetBeneficiaryOptions({
  currentEntityId,
  queryData,
  subformValues,
  isTwoClientHousehold,
  allowSurvivingSpouseOption,
}: UseGetBeneficiaryOptionsParams): UseGetBeneficiaryOptionsReturnValue {
  const selectedBeneficiaryIds = useMemo(() => {
    return subformValues?.map((b) => b.beneficiaryId) ?? [];
  }, [subformValues]);

  const household = first(getNodes(queryData?.households));

  const householdEntities = useMemo(() => {
    return getNodes(queryData?.entities)
      .filter((e) => e.stage !== EntityStage.PreCreated)
      .map((e) => ({
        value: e.id,
        display: e.subtype.displayName,
        disabled:
          selectedBeneficiaryIds.includes(e.id) || e.id === currentEntityId,
        groupName: 'Entities',
      }));
  }, [queryData?.entities, selectedBeneficiaryIds, currentEntityId]);

  const individualBeneficiaryOptions = useMemo(() => {
    return (household?.clientProfiles ?? []).map((b) => ({
      display: b.displayName,
      value: b.id,
      disabled: selectedBeneficiaryIds.includes(b.id),
      groupName: 'Individuals',
    }));
  }, [household?.clientProfiles, selectedBeneficiaryIds]);

  const organizationBeneficiaryOptions = useMemo(() => {
    return (household?.clientOrganizations ?? []).map((b) => ({
      display: b.name,
      value: b.id,
      disabled: selectedBeneficiaryIds.includes(b.id),
      groupName: 'Organizations',
    }));
  }, [household?.clientOrganizations, selectedBeneficiaryIds]);

  const testamentaryEntityBeneficiaryOptions = useMemo(() => {
    return getNodes(household?.testamentaryEntities).map((te) => {
      return {
        display: te.displayName,
        value: te.id,
        disabled: selectedBeneficiaryIds.includes(te.id),
        groupName: 'Sub-trusts',
        secondaryText:
          formatCreatedFromEntities(te.createdFromEntities) ?? undefined,
      };
    });
  }, [household?.testamentaryEntities, selectedBeneficiaryIds]);

  /**
   * Whether to show the "Surviving Spouse" option.
   * This option is ONLY shown for subtrust (testamentary entity) beneficiary forms
   * in two-grantor households, as it represents a dynamic placeholder that resolves
   * to whichever spouse survives.
   */
  const showSurvivingSpouseOption =
    isTwoClientHousehold && allowSurvivingSpouseOption;

  const survivingSpouseOption = useMemo(
    () =>
      showSurvivingSpouseOption
        ? {
            display: 'Surviving spouse',
            value: SURVIVING_SPOUSE_SENTINEL,
            groupName: 'Individuals',
            startAdornment: (
              <ListItemIcon>
                <User01Icon size={16} />
              </ListItemIcon>
            ),
          }
        : null,
    [showSurvivingSpouseOption]
  );

  const beneficiaryOptions = useMemo(() => {
    return [
      // Surviving spouse option appears first when available (only for subtrust beneficiaries)
      ...(survivingSpouseOption ? [survivingSpouseOption] : []),
      ...individualBeneficiaryOptions.sort((a, b) =>
        a.display.localeCompare(b.display)
      ),
      ...organizationBeneficiaryOptions.sort((a, b) =>
        a.display.localeCompare(b.display)
      ),
      ...householdEntities.sort((a, b) => a.display.localeCompare(b.display)),
      ...testamentaryEntityBeneficiaryOptions.sort((a, b) =>
        a.display.localeCompare(b.display)
      ),
    ];
  }, [
    survivingSpouseOption,
    individualBeneficiaryOptions,
    organizationBeneficiaryOptions,
    householdEntities,
    testamentaryEntityBeneficiaryOptions,
  ]);

  return { options: beneficiaryOptions };
}
